import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuthModal } from "@/context/AuthModalContext";
import { Menu, X } from "lucide-react";

const LOGO_URL =
  "https://customer-assets-agu9un31.emergentagent.net/job_140d9c64-db2c-4907-8f79-3f079e2743ed/artifacts/x1pnllq7_SRC%20Web%20icon%20%282%29.png";

const BASE_NAV_ITEMS = [
  { label: "Features", to: "/#features", type: "hash" },
  { label: "Marketplace", to: "/learn/marketplace" },
  { label: "Sponsorship", to: "/learn/sponsorship" },
  { label: "Price Compare", to: "/learn/price-compare" },
  { label: "Grid Points", to: "/learn/grid-points" },
  { label: "How it works", to: "/learn/how-it-works" },
];

/**
 * Marketing top bar used on the public Landing page and the /learn/* pages.
 * - Grows when at the very top of the page, shrinks on scroll.
 * - Preserves logo aspect ratio at every viewport width (uses object-contain + max-w cap).
 * - Collapses the nav into a hamburger menu below md breakpoint.
 */
export default function MarketingTopBar({ transparentAtTop = false, authButtons, compact = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { openAuth } = useAuthModal();

  const buttons = authButtons ?? [
    { label: "Sign in", mode: "login", variant: "ghost", testId: "marketing-signin" },
    { label: "Create account", mode: "register", variant: "primary", testId: "marketing-register" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const grown = !scrolled && !compact;

  const NAV_ITEMS = location.pathname === "/"
    ? BASE_NAV_ITEMS
    : [{ label: "Home", to: "/" }, ...BASE_NAV_ITEMS];

  const handleHashClick = (e, to) => {
    e.preventDefault();
    const [path, hash] = to.split("#");
    const targetPath = path || "/";
    if (location.pathname === targetPath) {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(to);
    }
    setOpen(false);
  };

  const isActive = (n) => {
    if (n.type === "hash") return false;
    if (n.to === "/") return location.pathname === "/";
    return location.pathname === n.to || location.pathname.startsWith(`${n.to}/`);
  };

  const renderNavLink = (n, variant) => {
    const testIdPrefix = variant === "drawer" ? "nav-drawer-" : "nav-";
    const testId = `${testIdPrefix}${n.label.toLowerCase().replace(/\s|&/g, "-")}`;
    const active = isActive(n);
    const cls = variant === "drawer"
      ? `rounded-md px-3 py-2 text-sm transition-colors ${active ? "bg-secondary text-foreground" : "text-foreground hover:bg-secondary/60"}`
      : `whitespace-nowrap rounded-full px-3 py-1.5 transition-colors ${active ? "bg-white/15 text-white" : "hover:text-foreground"}`;
    if (n.type === "hash") {
      return (
        <a key={n.label} href={n.to} onClick={(e) => handleHashClick(e, n.to)} className={cls} data-testid={testId}>
          {n.label}
        </a>
      );
    }
    return (
      <Link key={n.label} to={n.to} className={cls} onClick={() => setOpen(false)} data-testid={testId}>
        {n.label}
      </Link>
    );
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "glass" : transparentAtTop ? "bg-transparent" : "bg-background/70 backdrop-blur"
      }`}
    >
      <div className={`src-container relative mx-auto flex max-w-7xl items-center px-4 transition-all duration-300 sm:px-6 ${grown ? "h-24 sm:h-28" : "h-16"}`}>
        {/* Logo — absolute so it never affects the flex row */}
        <Link
          to="/"
          className={`absolute left-4 top-1/2 -translate-y-1/2 flex items-center transition-all duration-300 sm:left-6 ${
            grown ? "pointer-events-none z-0" : "pointer-events-auto z-20"
          }`}
          data-testid="marketing-logo"
        >
          <img
            src={LOGO_URL}
            alt="Sim Racing Connected"
            className={`w-auto select-none object-contain origin-top-left transition-all duration-300 ${
              grown
                ? "h-10 scale-[1.15] translate-y-[24px] sm:h-16 sm:scale-[1.5] sm:translate-y-[70px] lg:h-20 lg:scale-[1.8] lg:translate-y-[120px]"
                : "h-10 scale-100 translate-y-0"
            }`}
            draggable={false}
          />
        </Link>

        <nav
          className={`relative z-10 hidden items-center text-muted-foreground transition-all duration-300 lg:flex ${
            grown ? "ml-0 gap-8 text-xl font-semibold" : "ml-48 gap-7 text-sm"
          }`}
        >
          {NAV_ITEMS.map((n) => renderNavLink(n, "desktop"))}
        </nav>

        <div className="ml-auto flex flex-shrink-0 items-center gap-2">
          <div className="hidden items-center gap-2 sm:flex">
            {buttons.map((b) => (
              b.variant === "primary" ? (
                <Button key={b.label} onClick={() => openAuth(b.mode)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid={b.testId}>{b.label}</Button>
              ) : (
                <Button key={b.label} variant="ghost" onClick={() => openAuth(b.mode)} className="text-foreground hover:bg-secondary" data-testid={b.testId}>{b.label}</Button>
              )
            ))}
          </div>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-border text-foreground lg:hidden"
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="marketing-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile / narrow drawer */}
      {open && (
        <div className="lg:hidden" data-testid="marketing-menu-drawer">
          <div className="mx-4 mb-4 rounded-lg border border-border bg-card p-3 shadow-lg sm:mx-6">
            <nav className="flex flex-col">
              {NAV_ITEMS.map((n) => renderNavLink(n, "drawer"))}
              <div className="mt-2 flex flex-col gap-2 border-t border-border pt-3 sm:hidden">
                {buttons.map((b) => (
                  b.variant === "primary" ? (
                    <Button key={b.label} onClick={() => { setOpen(false); openAuth(b.mode); }} className="w-full bg-primary text-primary-foreground hover:bg-primary/90">{b.label}</Button>
                  ) : (
                    <Button key={b.label} variant="outline" onClick={() => { setOpen(false); openAuth(b.mode); }} className="w-full">{b.label}</Button>
                  )
                ))}
              </div>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}
